// convex/lib/config/domains/ai.ts

import { validatorUsers } from "../../../system/users";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// AI DOMAIN - Status Values for i18n
// ============================================================================

export const AI_INSIGHT_TYPES = [
  "anomaly",
  "trend",
  "recommendation",
  "alert",
  "summary",
] as const;
export const AI_INSIGHT_CATEGORIES = [
  "sales",
  "invoices",
  "inventory",
  "clients",
  "finance",
  "operations",
  "general",
] as const;
export const AI_INSIGHT_SEVERITIES = ["info", "warning", "critical"] as const;

export const aiDomain: DomainGroup = {
  id: "ai",
  // Solo cloud (saas/desktop): excluida de las instalaciones locales
  // (onprem y free_desktop), igual que reports/operations.
  editions: ["saas", "desktop"],
  name: "IA y Análisis",
  description: "Configuración del agente IA y análisis automáticos",
  icon: "Sparkles",
  order: 11,
  status: "active",

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "ai",
    keys: {
      title: "ai.title",
      description: "ai.description",
    },
  },

  modules: [
    {
      id: "ai_assistant",
      name: "Asistente",
      description: "Chat inteligente y asistente virtual",
      icon: "MessageSquare",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/ai"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 999,
        status: "active",
      },
      i18n: {
        namespace: "ai.ai_assistant",
        keys: {
          title: "ai.ai_assistant.title",
          description: "ai.ai_assistant.description",
          longDescription: "ai.ai_assistant.longDescription",
          searchPlaceholder: "ai.ai_assistant.searchPlaceholder",
          emptyState: "ai.ai_assistant.emptyState",
          actions: {
            view: "ai.ai_assistant.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },
      tables: [],
      readOnlyTables: [],
    },
    {
      id: "ai_insights",
      name: "Insights",
      description: "Analisis automaticos y recomendaciones",
      icon: "Lightbulb",
      permissions: ["canRead"],
      routes: ["/ai/insights"],
      navigation: {
        showInNav: true,
        contexts: ["main"],
        navOrder: 1000,
        status: "active",
      },
      i18n: {
        namespace: "ai.ai_insights",
        keys: {
          title: "ai.ai_insights.title",
          description: "ai.ai_insights.description",
          actions: {
            view: "ai.ai_insights.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },
      tables: [],
      readOnlyTables: [],
    },
    {
      id: "ai_settings",
      name: "Agente IA",
      description: "Configuración y gestión del asistente de IA",
      icon: "Sparkles",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/ai"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 999,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "ai.ai_settings",
        keys: {
          title: "ai.ai_settings.title",
          description: "ai.ai_settings.description",
          longDescription: "ai.ai_settings.longDescription",
          searchPlaceholder: "ai.ai_settings.searchPlaceholder",
          emptyState: "ai.ai_settings.emptyState",
          actions: {
            create: "ai.ai_settings.actions.create",
            edit: "ai.ai_settings.actions.edit",
            view: "ai.ai_settings.actions.view",
          },
        },
        translatableFields: ["customInstructions"],
        translatableEnums: [],
      },

      tables: [
        {
          tableName: "ai_agent_config",
          schema: {}, // Definida directamente en schema.ts como aiAgentConfigTable
          scope: "company" as TableScope,
          relations: [
            {
              key: "createdBy",
              sourceField: "internalControl.createdBy",
              schema: {},
              tableName: "users",
            },
            {
              key: "updatedBy",
              sourceField: "internalControl.updatedBy",
              schema: {},
              tableName: "users",
            },
          ],
          searchFields: [],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          tableName: "ai_insights",
          schema: {},
          scope: "company" as TableScope,
          relations: [
            {
              key: "createdBy",
              sourceField: "internalControl.createdBy",
              schema: {},
              tableName: "users",
            },
          ],
          searchFields: ["title", "category", "type"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          tableName: "ai_usage",
          schema: {},
          scope: "company" as TableScope,
          relations: [
            {
              key: "user",
              sourceField: "userId",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["model"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [],
    },
  ],
};
